

import * as React from 'react'
import { getContract, updateInvestmentAccount, updateInvestmentAmount, updateInvestmentKnowledgetest, updateInvestmentStep } from '../../globals/ApiHooks'
import { TranslatedTerms } from '../../globals/TranslatedTerms'
import { createEnDaContent, lang, translateContent } from '../../globals/Translations'
import { NiceNumberWithCurrency } from '../../shared/UIFormatting'
import { delay, IsMobile, useWindowSize } from '../../globals/UIHooks'
import Investment, { InvestmentStep } from '../../shared/Investment'
import Investor, { AccountId } from '../../shared/Investor'
import Project from '../../shared/Project'
import { DefaultCheckbox, defaultSelect, RegularMultilineInput, RegularTextInput, SelectInput, ToolTip } from '../atoms/Inputs'
import { CheckUncheckRow, Row } from '../atoms/ProjectCard'
import { EditInvestor } from '../organisms/EditInvestor'
import { RISK_LINK, TERMS_LINK } from '../organisms/Menu'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { ColumnFlexBox, RowFlexBox } from '../templates/Wrappers'
import PopupMessage from './PopupMessage'
import NewInvestmentInput from '../atoms/NewInvestmentInput'
import { DefaultPersonalAccountId, getFBDateNow } from '../../shared/Globals'
import ContractType from '../../shared/Contract'
import { TranslatedContent } from '../../shared/Language'
import { getInvestmentPaymentReference } from '../../shared/BusinessIntelligence'
import { useHistory } from 'react-router-dom'
import colors from '../../globals/colors'
import { InvestmentExperience, InvestmentExperienceEducationalLevelOption, InvestmentExperienceEducationalLevelOptionList, InvestmentKnowledgeOption, InvestmentKnowledgeOptionList, KnowledgeTest, RiskProfile, RiskProfileCalculation, RiskProfileExpenses, calculateInvestorRiskProfile } from '../../shared/InvestmentKnowledge'
import { CalculationResultsStep, IntervalItem, InvestorTestCalculationResult, InvestorTestElement, interval100, intervalIncome, intervalLarge, intervalMedium, intervalMonths, intervalXLarge } from './InvestorTestElements'
import { showError } from '../../globals/ErrorLib'

const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: "repeat(2, 1fr)", gridColumnGap: 20 }

const flex: React.CSSProperties = { display: 'flex', flexDirection: 'column-reverse' }

const buttonStyle = { marginTop: 10, marginBottom: 10 }

export type DefaultProps = {
    asyncTask: (executeable: () => Promise<void>) => () => Promise<void>,
    project: Project,
    investment: Investment,
    investor: Investor
}


export const InvestmentAmount: React.FC<DefaultProps> = ({ project, investment, asyncTask }) => {
    const history = useHistory()

    const MIN_INVESTEMENT = project.finance.minimumInvestment
    const MAX_INVESTMENT = project.finance.targetFunding - project.finance.currentFunding
    const DEFAULT_VALUE = investment.amount || MIN_INVESTEMENT
    const [message, setMessage] = React.useState<string | undefined>()

    const [value, setValue] = React.useState<number>(DEFAULT_VALUE)


    const handleSubmit = asyncTask(async () => {
        if (value < MIN_INVESTEMENT) setMessage(lang(TranslatedTerms.AMOUNT_INSUFFICIENT))
        else if (value > MAX_INVESTMENT) setMessage(lang(TranslatedTerms.AMOUNT_INSUFFICIENT))
        else await updateInvestmentAmount(investment.investmentId, { amount: value })
    })

    const handleBack = () => {
        history.push("/projects/" + project.projectId)
    }


    return (
        <Template titleTerm={TranslatedTerms.INVESTMENT_STEP_AMOUNT_TITLE} descriptionTerm={TranslatedTerms.INVESTMENT_STEP_AMOUNT_DESCRIPTION}
            next={handleSubmit} prev={handleBack}>
            {message && <PopupMessage message={message} />}
            <NewInvestmentInput project={project} setValue={setValue} defaultValue={DEFAULT_VALUE} />
        </Template>
    )
}


export const KYCData: React.FC<DefaultProps> = ({ asyncTask, investment, investor }) => {
    const [activeStep, setActiveStep] = React.useState<number>(0)
    const preSelectedAccountIndex = investor.accounts.findIndex((item) => item.accountId === investment.investorAccountId)
    const [selectedAccountId, setSelectedAccountId] = React.useState<AccountId>(DefaultPersonalAccountId)

    const handleSubmit = asyncTask(async () => {
        await updateInvestmentAccount(investment.investmentId, { accountId: selectedAccountId })
    })

    const handleBack = asyncTask(async () => {
        await updateInvestmentStep(investment.investmentId, { newStep: InvestmentStep.InvestmentAmountStep })
    })


    return (
        <Template titleTerm={TranslatedTerms.INVESTMENT_STEP_KYC_TITLE} descriptionTerm={TranslatedTerms.INVESTMENT_STEP_KYC_DESCRIPTION}
            next={handleSubmit} prev={handleBack} hideButtons={activeStep < 1}>
            <ColumnFlexBox style={{ marginBottom: 40 }}>
                <EditInvestor controllActiveStep={setActiveStep} controllSelectedAccount={setSelectedAccountId} preSelectedAccountIndex={preSelectedAccountIndex} />
            </ColumnFlexBox>
        </Template>
    )
}

export const Terms: React.FC<DefaultProps> = (props) => {
    const [risk, setRisk] = React.useState<boolean>(false)
    const [policy, setPolicy] = React.useState<boolean>(false)
    const [marketing, setMarketing] = React.useState<boolean>(false)

    const DeveloperCompany = props && props.project && props.project.nonLocalizedContent && props.project.nonLocalizedContent.developer || "developer"
    const riskWithDevName = lang(TranslatedTerms.INVESTMENT_STEP_TERMS_RISK).split('{developer}').join(DeveloperCompany)
    const riskDescriptionWithDevName = lang(TranslatedTerms.INVESTMENT_STEP_TERMS_DESCRIPTION).split('{developer}').join(DeveloperCompany)

    return (
        <TemplateWithButtons {...props} title={TranslatedTerms.INVESTMENT_STEP_TERMS_TITLE}
            description={riskDescriptionWithDevName}
            nextStep={InvestmentStep.InvestorTest} prevStep={InvestmentStep.KYCDataStep} disableNext={!risk || !policy || !marketing}>
            <ColumnFlexBox>
                <DefaultCheckbox description={(<span>{riskWithDevName} <a href={RISK_LINK} target='_blank'>{lang(TranslatedTerms.READ_MORE)}</a></span>)}
                    handleClick={() => setRisk(!risk)} />
                <DefaultCheckbox description={(<span>{lang(TranslatedTerms.INVESTMENT_STEP_TERMS_POLICY)}
                    <a href={TERMS_LINK} target='_blank'>{lang(TranslatedTerms.READ_MORE)}</a></span>)}
                    handleClick={() => setPolicy(!policy)} />
                <DefaultCheckbox description={lang(TranslatedTerms.INVESTMENT_STEP_TERMS_MARKETING)} handleClick={() => setMarketing(!marketing)} />
            </ColumnFlexBox>
        </TemplateWithButtons>
    )
}

const CreateInvestorExperienceItem: React.FC<{ existingInvestmentExperiences: InvestmentExperience[], addInvestmentExperience(experience: InvestmentExperience): void }> = ({ existingInvestmentExperiences, addInvestmentExperience }) => {
    const [showCreateButton, setShowCreateButton] = React.useState(true)
    const [investmentKnowledgeOption, setInvestmentKnowledgeOption] = React.useState<InvestmentKnowledgeOption | string>()

    const [investmentKnowledgeQuantity, setInvestmentKnowledgeQuantity] = React.useState<number | undefined>()
    const [investmentKnowledgeAmount, setInvestmentKnowledgeAmount] = React.useState<number | undefined>()
    const disableNext = !investmentKnowledgeAmount || !investmentKnowledgeQuantity || !investmentKnowledgeOption || investmentKnowledgeOption === defaultSelect
    const onComplete = () => {
        if (disableNext) return
        const experience: InvestmentExperience = {
            amount: investmentKnowledgeAmount,
            quantity: investmentKnowledgeQuantity,
            //@ts-ignore
            option: investmentKnowledgeOption
        }
        addInvestmentExperience(experience)
        setInvestmentKnowledgeOption(undefined)
        setInvestmentKnowledgeQuantity(undefined)
        setInvestmentKnowledgeAmount(undefined)
        setShowCreateButton(true)
    }
    const availableInvestmentKnowledgeOptionList = InvestmentKnowledgeOptionList.filter((option) => !existingInvestmentExperiences.find((item) => item.option === option))

    if (availableInvestmentKnowledgeOptionList.length === 0) return <></>
    if (showCreateButton) return (
        <ColumnFlexBox>
            {existingInvestmentExperiences.length === 0 && <h5 style={{ textAlign: 'center' }}>
                {translateContent(createEnDaContent("No investment experience added", "Ingen Investeringserfaring tilføjet"))}</h5>}
            <button style={{ ...buttonStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setShowCreateButton(false)}>
                <AddCircleOutlineIcon style={{ fontSize: 16, marginRight: 6 }} /> {translateContent(createEnDaContent("Add investment experience", "Tilføj investeringserfaring"))}</button>
        </ColumnFlexBox>
    )


    return (<>
        <SelectInput options={availableInvestmentKnowledgeOptionList}
            fieldName={translateContent(createEnDaContent("What types of investments do you have experience with?", "Hvilke typer investeringer har du erfaring med?"))}
            itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={investmentKnowledgeOption}
            onSelect={(e: InvestmentKnowledgeOption) => setInvestmentKnowledgeOption(e)} />
        {investmentKnowledgeOption && investmentKnowledgeOption !== defaultSelect && (
            <>
                <SelectInput options={interval100}
                    fieldName={translateContent(createEnDaContent(
                        "How many times have you invested in " + lang(investmentKnowledgeOption) + " within the last 5 years?",
                        "Hvor mange gange har du investeret i " + lang(investmentKnowledgeOption) + " indenfor de sidste 5 år?"))}
                    itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()} value={investmentKnowledgeQuantity?.toString()}
                    onSelect={(e: string) => setInvestmentKnowledgeQuantity(Number(e))} />
                <SelectInput options={intervalXLarge}
                    fieldName={translateContent(createEnDaContent(
                        "What is the total amount you have invested in " + lang(investmentKnowledgeOption) + " within the last 5 years?",
                        "Hvor stort et beløb har du i alt investeret i " + lang(investmentKnowledgeOption) + " indenfor de sidste 5 år?"))}
                    itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                    value={investmentKnowledgeAmount?.toString()} onSelect={(e: string) => setInvestmentKnowledgeAmount(Number(e))} />
                <button style={{ ...buttonStyle }} className={disableNext ? "disable" : ""} onClick={onComplete}>{lang(TranslatedTerms.ADD)}</button>
            </>
        )}
    </>)
}

const InvestorExperienceItem: React.FC<{ investmentExperience: InvestmentExperience, onDelete(): void }> = ({ investmentExperience, onDelete }) => {
    const { width } = useWindowSize()
    const mobile = IsMobile(width)

    return (
        <RowFlexBox style={{ backgroundColor: colors.lighter, paddingLeft: mobile ? 16 : 32, paddingRight: mobile ? 16 : 32, paddingTop: 12, paddingBottom: 12, marginTop: 12, marginBottom: 12, borderRadius: 10, justifyContent: 'space-between' }}>
            <ColumnFlexBox>
                <h5>{lang(investmentExperience.option)}</h5>
                <span style={{ color: colors.grayText }}>{translateContent(createEnDaContent("Quantity last 5 years", "Antal gange sidste 5 år"))}: <b style={{ color: colors.dark }}>{investmentExperience.quantity} {translateContent(createEnDaContent("times", "gange"))}</b></span>
                <span style={{ color: colors.grayText }}>{translateContent(createEnDaContent("Amount last 5 years", "Beløb sidste 5 år"))}: <b style={{ color: colors.dark }}>{intervalXLarge.find((item) => item.value === investmentExperience.amount)?.displayName}</b></span>
            </ColumnFlexBox>
            <ColumnFlexBox style={{ justifyContent: 'flex-end' }}><button onClick={onDelete} style={{ marginLeft: 4, marginRight: 4, backgroundColor: colors.danger, color: colors.lightest }}>{lang(TranslatedTerms.DELETE)}</button></ColumnFlexBox>
        </RowFlexBox>
    )
}

enum KnowledgeTestStep {
    SIMULATION = "SIMULATION",
    SIMULATION_RESULT = "SIMULATION_RESULT",
    KNOWLEDGE_TEST = "KNOWLEDGE_TEST",
    KNOWLEDGE_RESULT = "KNOWLEDGE_RESULT"
}

export const InvestorTest: React.FC<DefaultProps> = (props) => {
    const investmentAccount = props.investor.accounts.find((item) => item.accountId === props.investment.investorAccountId)
    const investmentAccountTest = investmentAccount && investmentAccount.knowledgeTest

    const [knowledgeTestStep, setKnowledgeTestStep] = React.useState<KnowledgeTestStep>(KnowledgeTestStep.SIMULATION)
    const [updatedKnowledgeTest, setUpdatedKnowledgeTest] = React.useState<KnowledgeTest>()

    const [riskProfileCalculations, setRiskProfileCalculations] = React.useState<RiskProfileCalculation | undefined>()

    const [acceptSimulation, setAcceptSimulation] = React.useState<boolean>(false)
    const [acceptRiskWarning, setAcceptRiskWarning] = React.useState<boolean>(false)

    const [investmentExperiences, setInvestmentExperiences] = React.useState<InvestmentExperience[]>(investmentAccountTest?.investmentExperiences || [])

    const [investmentExperienceEducationalLevelOption, setInvestmentExperienceEducationalLevelOption] = React.useState<InvestmentExperienceEducationalLevelOption | string | undefined>(investmentAccountTest?.educationalLevel)

    const [purpose, setPurpose] = React.useState<string | undefined>(investmentAccountTest?.investmentPurpose.purpose)
    const [duration, setDuration] = React.useState<string | undefined>(investmentAccountTest?.investmentPurpose.duration)
    const [profile, setProfile] = React.useState<string | undefined>(investmentAccountTest?.investmentPurpose.profile)
    const [sustainable, setSustainable] = React.useState<string | undefined>(investmentAccountTest?.investmentPurpose.substainable)

    const [salary, setSalary] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileIncome.salary)
    const [interestIncome, setInterestIncome] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileIncome.interestIncome)
    const [dividendIncome, setDividendIncome] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileIncome.dividendIncome)
    const [realEstateIncome, setRealEstateIncome] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileIncome.realEstateIncome)

    const [pensionAssets, setPensionAssets] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileAssets.pensionAssets)
    const [stockAssets, setStockAssets] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileAssets.stockAssets)
    const [bondAssets, setBondAssets] = React.useState<number | undefined>(investmentAccountTest?.riskProfile.riskProfileAssets.bondAssets)


    const [childSupport, setChildSupport] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.childSupport)
    const [mortgage, setMortgage] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.mortgage)
    const [otherLoans, setOtherLoans] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.otherLoans)
    const [insurances, setInsurances] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.insurances)
    const [utilities, setUtilities] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.utilities)
    const [subscriptionServices, setSubscriptionServices] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.subscriptionServices)
    const [incometaxAndPropertyTax, setIncometaxAndPropertyTax] = React.useState<number | undefined>(investmentAccountTest?.riskProfileExpenses.incometaxAndPropertyTax)

    const [shareDataWithFundbricks, setShareDataWithFundbricks] = React.useState<boolean>(!!investmentAccountTest)

    const validSimulationResult =
        sustainable !== undefined && salary !== undefined && interestIncome !== undefined && dividendIncome !== undefined && realEstateIncome !== undefined &&
        pensionAssets !== undefined && stockAssets !== undefined && bondAssets !== undefined && childSupport !== undefined && mortgage !== undefined &&
        otherLoans !== undefined && insurances !== undefined && utilities !== undefined && subscriptionServices !== undefined && incometaxAndPropertyTax !== undefined

    const validInvestorTestResult = investmentExperienceEducationalLevelOption !== undefined && purpose !== undefined && duration !== undefined && profile !== undefined


    const handleNext = (nextStep?: KnowledgeTestStep) => props.asyncTask(async () => {
        console.log(nextStep)
        if (nextStep === KnowledgeTestStep.KNOWLEDGE_RESULT) {
            if (!!validSimulationResult && !!validInvestorTestResult) {

                const knowledgeTest: KnowledgeTest = {
                    investmentExperiences: investmentExperiences,
                    //@ts-ignore
                    educationalLevel: investmentExperienceEducationalLevelOption,
                    investmentPurpose: { purpose, duration, profile, substainable: sustainable },
                    riskProfile: {
                        riskProfileIncome: { salary, interestIncome, dividendIncome, realEstateIncome },
                        riskProfileAssets: { pensionAssets, stockAssets, bondAssets }
                    },
                    riskProfileExpenses: { childSupport, mortgage, otherLoans, insurances, utilities, subscriptionServices, incometaxAndPropertyTax },
                    completedDate: getFBDateNow()
                }
                if (!updatedKnowledgeTest) {
                    setUpdatedKnowledgeTest(knowledgeTest)
                }
                setKnowledgeTestStep(nextStep)
            } else {
                showError("An error occurred")
            }
        } else if (!!validSimulationResult && nextStep === KnowledgeTestStep.SIMULATION_RESULT) {
            setKnowledgeTestStep(nextStep)
            const riskProfile: RiskProfile = {
                riskProfileIncome: { salary, interestIncome, dividendIncome, realEstateIncome },
                riskProfileAssets: { pensionAssets, stockAssets, bondAssets }
            }
            const riskProfileExpenses: RiskProfileExpenses = { childSupport, mortgage, otherLoans, insurances, utilities, subscriptionServices, incometaxAndPropertyTax }

            const newRiskProfileCalculation = calculateInvestorRiskProfile(riskProfile, riskProfileExpenses)
            setRiskProfileCalculations(newRiskProfileCalculation)

        }
        else if (nextStep) setKnowledgeTestStep(nextStep)
        else if (!!validSimulationResult && !!validInvestorTestResult) {
            const knowledgeTest: KnowledgeTest = {
                investmentExperiences: investmentExperiences,
                //@ts-ignore
                educationalLevel: investmentExperienceEducationalLevelOption,
                investmentPurpose: { purpose, duration, profile, substainable: sustainable },
                riskProfile: {
                    riskProfileIncome: { salary, interestIncome, dividendIncome, realEstateIncome },
                    riskProfileAssets: { pensionAssets, stockAssets, bondAssets }
                },
                riskProfileExpenses: { childSupport, mortgage, otherLoans, insurances, utilities, subscriptionServices, incometaxAndPropertyTax },
                completedDate: getFBDateNow()
            }
            if (shareDataWithFundbricks) await updateInvestmentKnowledgetest(props.investment.investmentId, { knowledgeTest })
            else await updateInvestmentKnowledgetest(props.investment.investmentId, {})

        }
    })

    const handleBack = props.asyncTask(async () => {
        if (knowledgeTestStep === KnowledgeTestStep.SIMULATION) await updateInvestmentStep(props.investment.investmentId, { newStep: InvestmentStep.AcceptTerms })
        else if (knowledgeTestStep === KnowledgeTestStep.SIMULATION_RESULT) setKnowledgeTestStep(KnowledgeTestStep.SIMULATION)
        else if (knowledgeTestStep === KnowledgeTestStep.KNOWLEDGE_TEST) setKnowledgeTestStep(KnowledgeTestStep.SIMULATION_RESULT)
        else if (knowledgeTestStep === KnowledgeTestStep.KNOWLEDGE_RESULT) setKnowledgeTestStep(KnowledgeTestStep.KNOWLEDGE_TEST)
    })

    const handleMoreExperiencedFormPress = () => {
        delay(500)
        window.open("https://prod-attachments-bucket-fb-2-0.s3.eu-west-1.amazonaws.com/9d466dbd-48f1-4859-b6db-6c5b801100f3.pdf", "_blank");
    }

    if (knowledgeTestStep === KnowledgeTestStep.KNOWLEDGE_RESULT && updatedKnowledgeTest) {
        const result = calculateInvestorRiskProfile(
            updatedKnowledgeTest.riskProfile,
            updatedKnowledgeTest.riskProfileExpenses,
            updatedKnowledgeTest.investmentExperiences,
            updatedKnowledgeTest.educationalLevel)

        return (
            <Template {...props} titleTerm={TranslatedTerms.RISK_WARNING} disableNext={!acceptRiskWarning}
                next={handleNext()} prev={handleBack}>
                <ColumnFlexBox style={{ marginBottom: 32, width: '100%' }}>
                    <InvestorTestCalculationResult calculations={result} riskProfileString={profile} step={CalculationResultsStep.KNOWLEDGE_TEST} />
                    <button style={{ marginTop: 32, marginBottom: 32 }} className="light" onClick={handleMoreExperiencedFormPress}>{lang(TranslatedTerms.APPLY_FOR_MORE_EXPERIENCED_BUTTON)}</button>
                    <DefaultCheckbox checked={acceptRiskWarning} description={<p style={{ fontSize: 16, lineHeight: '1.545em' }}><b>{lang(TranslatedTerms.RISK_WARNING_LESS_EXPERIENCED_ACCEPTENCE)}</b></p>} handleClick={() => setAcceptRiskWarning(!acceptRiskWarning)} />
                </ColumnFlexBox>
            </Template>
        )
    }

    if (knowledgeTestStep === KnowledgeTestStep.SIMULATION_RESULT) {
        return (
            <Template {...props} titleTerm={TranslatedTerms.SIMULATION_RESULT} disableNext={!acceptSimulation} descriptionTerm={TranslatedTerms.INVESTMENT_STEP_INVESTOR_TEST_DESCRIPTION}
                next={handleNext(KnowledgeTestStep.KNOWLEDGE_TEST)} prev={handleBack}>
                <ColumnFlexBox style={{ marginBottom: 32, width: '100%' }}>
                    {riskProfileCalculations && <InvestorTestCalculationResult calculations={riskProfileCalculations} ignoreExperienceAndEducation={true} step={CalculationResultsStep.ASSETS} />}
                    <DefaultCheckbox checked={acceptSimulation} description={<p style={{ fontSize: 16, lineHeight: '1.545em' }}><b>{translateContent(createEnDaContent("I acknowledge having completed and received the result of the simulation of my net worth and ability to bear loss.", "Jeg anerkender at have gennemført og modtaget resultat af simulering af min nettoformue og evne til at bære tab."))}</b></p>} 
                    handleClick={() => setAcceptSimulation(!acceptSimulation)} />

                </ColumnFlexBox>
            </Template>
        )
    }


    if (knowledgeTestStep === KnowledgeTestStep.KNOWLEDGE_TEST) return (
        <Template {...props} titleTerm={TranslatedTerms.INVESTMENT_STEP_INVESTOR_TEST_TITLE}
            descriptionTerm={TranslatedTerms.INVESTMENT_STEP_INVESTOR_TEST_DESCRIPTION} disableNext={!validInvestorTestResult}
            next={handleNext(KnowledgeTestStep.KNOWLEDGE_RESULT)} prev={handleBack}>
            <ColumnFlexBox style={{ marginBottom: 32, width: '100%' }}>
                <h3>{translateContent(createEnDaContent("Investment Experience", "Investeringserfaring"))}</h3>
                {investmentExperiences.length > 0 && investmentExperiences.map((item, key) => <InvestorExperienceItem investmentExperience={item} key={key}
                    onDelete={() => setInvestmentExperiences((list) => list.filter((li) => li.option !== item.option))} />)}
                <CreateInvestorExperienceItem existingInvestmentExperiences={investmentExperiences} addInvestmentExperience={(experience) => setInvestmentExperiences((existing) => [...existing, experience])} />
                <h3 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("Education Level", "Uddannelsesniveau"))}</h3>
                <SelectInput options={InvestmentExperienceEducationalLevelOptionList}
                    fieldName={translateContent(createEnDaContent("Which education is the highest you have completed?", "Hvilken uddannelse er den højeste, du har afsluttet?"))}
                    itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={investmentExperienceEducationalLevelOption}
                    onSelect={(e: InvestmentKnowledgeOption) => setInvestmentExperienceEducationalLevelOption(e)} />
                <h3 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("Purpose, time horizon and investment profile", "Formål, tidshorisont og investeringsprofil"))}</h3>
                <SelectInput options={[TranslatedTerms.SAVINGS, TranslatedTerms.PENSION, TranslatedTerms.OTHER]}
                    fieldName={translateContent(createEnDaContent("What is the purpose of your investment?", "Hvad er formålet med din investering?"))}
                    itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={purpose}
                    onSelect={(e: string) => setPurpose(e)} />
                <SelectInput options={intervalMonths}
                    fieldName={translateContent(createEnDaContent("How long do you expect to keep your investment?", "Hvor lang forventer du at beholde din investering?"))}
                    itemRender={(e: string) => e + " " + lang(TranslatedTerms.MTH).toLowerCase()} getValue={(e: string) => e} value={duration}
                    onSelect={setDuration} />
                <SelectInput options={[TranslatedTerms.LOW, TranslatedTerms.MEDIUM, TranslatedTerms.HIGH, TranslatedTerms.DONTKNOW]}
                    fieldName={translateContent(createEnDaContent("How would you describe your risk profile?", "Hvordan vil du beskrive din risikoprofil?"))}
                    itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={profile}
                    onSelect={setProfile} />
                <SelectInput options={[TranslatedTerms.YES, TranslatedTerms.NO, TranslatedTerms.DONTKNOW]}
                    fieldName={translateContent(createEnDaContent("Is it important to you that your investments are sustainable?", "Er det vigtigt for dig at dine investeringer er bæredygtige?"))}
                    itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={sustainable}
                    onSelect={setSustainable} />
                <h3 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("Data and privacy", "Data og privatliv"))}</h3>
                <SelectInput options={[TranslatedTerms.YES, TranslatedTerms.NO]}
                    fieldName={translateContent(createEnDaContent("Save for next time and share Fundbrikcs.com", "Gem til næste gang og del oplysninger med Fundbricks.com"))}
                    itemRender={(e: string) => lang(e)} getValue={(e: string) => e} value={investmentAccountTest ? TranslatedTerms.YES : undefined}
                    onSelect={(e: string) => setShareDataWithFundbricks(e === TranslatedTerms.YES)} />
            </ColumnFlexBox>
        </Template>
    )

    return (
        <Template {...props} titleTerm={TranslatedTerms.INVESTMENT_STEP_INVESTOR_SIMULATION_TITLE}
            descriptionTerm={TranslatedTerms.INVESTMENT_STEP_INVESTOR_SIMULATION_DESCRIPTION} disableNext={!validSimulationResult}
            next={handleNext(KnowledgeTestStep.SIMULATION_RESULT)} prev={handleBack}>
            <ColumnFlexBox style={{ marginBottom: 32, width: '100%' }}>
                <InvestorTestElement
                    salary={salary}
                    setSalary={setSalary}
                    interestIncome={interestIncome}
                    setInterestIncome={setInterestIncome}
                    dividendIncome={dividendIncome}
                    setDividendIncome={setDividendIncome}
                    realEstateIncome={realEstateIncome}
                    setRealEstateIncome={setRealEstateIncome}
                    pensionAssets={pensionAssets}
                    setPensionAssets={setPensionAssets}
                    stockAssets={stockAssets}
                    setStockAssets={setStockAssets}
                    bondAssets={bondAssets}
                    setBondAssets={setBondAssets}
                    childSupport={childSupport}
                    setChildSupport={setChildSupport}
                    mortgage={mortgage}
                    setMortgage={setMortgage}
                    otherLoans={otherLoans}
                    setOtherLoans={setOtherLoans}
                    insurances={insurances}
                    setInsurances={setInsurances}
                    utilities={utilities}
                    setUtilities={setUtilities}
                    subscriptionServices={subscriptionServices}
                    setSubscriptionServices={setSubscriptionServices}
                    incometaxAndPropertyTax={incometaxAndPropertyTax}
                    setIncometaxAndPropertyTax={setIncometaxAndPropertyTax}
                />
            </ColumnFlexBox>
        </Template>
    )

}

export const getContractRow = (projectTitle: TranslatedContent, openContract: (index: number) => () => void) => (contract: ContractType, key: number) => {

    const contractStatus = contract.signedContractDate ? lang(TranslatedTerms.CONTRACT_SIGNED) : <a onClick={openContract(key)}>{lang(TranslatedTerms.SIGN_CONTRACT)}</a>

    return Row('[' + translateContent(projectTitle) + '] ' + lang(TranslatedTerms.CONTRACT) + ' ' + (key + 1), contractStatus, key)
}

export const Contract: React.FC<DefaultProps> = ({ project, asyncTask, investment }) => {
    const contract = investment.investmentTerms.contracts[0]
    const [contractLink, setContractlink] = React.useState<string | undefined>(contract ? contract.contractURL : undefined)
    const [errorMessage, setErrorMessage] = React.useState<string>()
    const handleBack = asyncTask(async () => {
        await updateInvestmentStep(investment.investmentId, { newStep: InvestmentStep.InvestorTest })
    })
    const reload = () => window.location.reload()

    const openContract = (index: number) => asyncTask(async () => {
        setErrorMessage(undefined)
        const result = await getContract(investment.investmentId, { index })
        if (result) {
            setContractlink(result.link)

            //@ts-ignore
            window.dataLayer = window.dataLayer || [];
            //@ts-ignore
            window.dataLayer.push({
                'event': 'investment_contract_started',
                'projectId': investment.projectId,
                'investmentId': investment.investmentId,
                'investorId': investment.investorId,
                'amount': investment.amount
            });
            delay(500)
            window.open(result?.link || "", '_blank')
        } else {
            setErrorMessage("Contract could not be opened")
        }
    })

    const DeveloperCompany = project.nonLocalizedContent && project.nonLocalizedContent.developer || "developer"

    const DescriptionWithDev = lang(TranslatedTerms.INVESTMENT_STEP_SIGN_DESCRIPTION).split("{developer}").join(DeveloperCompany)
    return (
        <Template titleTerm={TranslatedTerms.INVESTMENT_STEP_SIGN_TITLE} descriptionTerm={DescriptionWithDev}
            next={openContract(0)} prev={handleBack} nextButtonTitle={lang(TranslatedTerms.OPEN_CONTRACT)}>
            <ColumnFlexBox>
                <a onClick={reload} className="light">{lang(TranslatedTerms.RELOAD)}</a>
                {investment.investmentTerms.contracts.map(getContractRow(project.projectTitle, openContract))}
                {contractLink && <a style={{ marginTop: 10, fontSize: 12 }} href={contractLink} target="_blank">{lang(TranslatedTerms.PROBLEMS_OPENING_THE_LINK_CLICK_HERE)}</a>}
                {contractLink && <p style={{ marginTop: 0, fontSize: 12 }}>{contractLink}</p>}
                {errorMessage && <p style={{ color: colors.danger }}>{errorMessage}</p>}
            </ColumnFlexBox>
        </Template>
    )
}

const RefrenceTooltip: React.FC<{ investment: Investment, project: Project }> = ({ investment, project }) => {
    const globalReference = project.nonLocalizedContent && project.nonLocalizedContent.paymentRefrence
    return (
        <RowFlexBox>
            <ToolTip content={lang(TranslatedTerms.PAYMENT_REFERENCE_TOOLTIP)} />
            {globalReference && globalReference + " - "}
            {getInvestmentPaymentReference(investment)}
        </RowFlexBox>
    )
}

export const Transfer: React.FC<DefaultProps> = ({ project, investment }) => {
    const nonLocalized = project.nonLocalizedContent
    return (
        <Template titleTerm={TranslatedTerms.INVESTMENT_STEP_PAYMENT_TITLE} descriptionTerm={TranslatedTerms.INVESTMENT_STEP_PAYMENT_DESCRIPTION} hideButtons={true}>
            <ColumnFlexBox>
                {Row(lang(TranslatedTerms.AMOUNT), NiceNumberWithCurrency(investment.amount), 0)}
                {Row(lang(TranslatedTerms.BANK), nonLocalized && nonLocalized.bankAccountName || "", 1)}
                {Row(lang(TranslatedTerms.BANK_ACCOUNT), nonLocalized && nonLocalized.bankAccount || "", 2)}
                {Row(lang(TranslatedTerms.PAYMENT_REFERENCE), <RefrenceTooltip investment={investment} project={project} />, 3)}
                {Row(lang(TranslatedTerms.PAYMENT_DEADLINE), lang(TranslatedTerms.PAYMENT_DEADLINE_DESCRIPTION), 4)}
            </ColumnFlexBox>
        </Template>
    )
}

export const Congratulations: React.FC<DefaultProps> = ({ asyncTask, investment }) => {
    const history = useHistory()
    const mobile = IsMobile(useWindowSize().width)
    const goToDashboard = asyncTask(async () => {
        history.push("/dashboard")
    })
    const handleSubmit = asyncTask(async () => {
        await updateInvestmentStep(investment.investmentId, { newStep: InvestmentStep.InvestmentDetails })
        //@ts-ignore
        window.dataLayer = window.dataLayer || [];
        //@ts-ignore
        window.dataLayer.push({
            'event': 'investment_completed',
            'projectId': investment.projectId,
            'investmentId': investment.investmentId,
            'investorId': investment.investorId,
            'amount': investment.amount
        });
        delay(500)
        window.location.reload()
    })
    return (
        <Template titleTerm={TranslatedTerms.INVESTMENT_STEP_SUCESS_TITLE} descriptionTerm={TranslatedTerms.INVESTMENT_STEP_SUCESS_DESCRIPTION} hideButtons={true}>
            <ColumnFlexBox style={{ marginBottom: 30 }}>
                <p className="small light">{lang(TranslatedTerms.INVESTMENT_STEP_SUB_SUCESS_DESCRIPTION)}</p>
            </ColumnFlexBox>
            <div style={mobile ? flex : grid}>
                <button style={buttonStyle} onClick={goToDashboard} className="light">{lang(TranslatedTerms.GO_TO_DASHBOARD).toUpperCase()}</button>
                <button style={buttonStyle} onClick={handleSubmit}>{lang(TranslatedTerms.GO_TO_DETAILS).toUpperCase()}</button>
            </div>
        </Template>
    )
}

type TemplateWithButtonsProps = { nextStep: InvestmentStep, prevStep: InvestmentStep, title: TranslatedTerms, description: TranslatedTerms | string, hideButtons?: boolean, disableNext?: boolean }

export const TemplateWithButtons: React.FC<DefaultProps & TemplateWithButtonsProps> =
    ({ asyncTask, investment, nextStep, prevStep, children, hideButtons, disableNext, title, description }) => {

        const handleSubmit = asyncTask(async () => {
            await updateInvestmentStep(investment.investmentId, { newStep: nextStep })
        })

        const handleBack = asyncTask(async () => {
            await updateInvestmentStep(investment.investmentId, { newStep: prevStep })
        })

        return (
            <Template titleTerm={title} descriptionTerm={description}
                next={handleSubmit} prev={handleBack} hideButtons={hideButtons} disableNext={disableNext}>
                <ColumnFlexBox>
                    {children}
                </ColumnFlexBox>
            </Template>
        )
    }

type TemplateProps = {
    titleTerm: TranslatedTerms, descriptionTerm?: TranslatedTerms | string, prev?: () => void, next?: () => void,
    hideButtons?: boolean, nextButtonTitle?: string, disableNext?: boolean
}


const Template: React.FC<TemplateProps> = ({ titleTerm, descriptionTerm, children, prev, next, hideButtons, nextButtonTitle, disableNext }) => {
    const mobile = IsMobile(useWindowSize().width)

    const nextHandle = !disableNext ? next : () => { }

    return (
        <div style={{ maxWidth: 800, marginLeft: 'auto', marginRight: 'auto', marginTop: 20, padding: 10, paddingBottom: 100 }}>
            <div style={{ maxWidth: 800 }}>
                <h1>{lang(titleTerm)}</h1>
                <p className="light">{lang(descriptionTerm)}</p>
                <div style={{ marginTop: 20 }}>
                    {children}
                </div>
                {!hideButtons && (
                    <div style={mobile ? flex : grid}>
                        <button style={buttonStyle} onClick={prev} className="light">{lang(TranslatedTerms.BACK).toUpperCase()}</button>
                        <button style={buttonStyle} onClick={nextHandle} className={disableNext ? "disable" : ""}>{nextButtonTitle || lang(TranslatedTerms.NEXT).toUpperCase()}</button>
                    </div>
                )}
            </div>
        </div>
    )
}
